import { db } from '../db';
import { doRequest } from './../http';

// Model
import type {
  Formas,
  FormasPagamento,
  Vistoria,
  VistoriaConsultor,
  VistoriaExecucao,
} from './../../model/vistoria.interface';
import type {
  VistoriaEtapaDadosCnh,
  VistoriaEtapaDadosEndereco,
  VistoriaEtapaDadosFinanceiros,
  VistoriaEtapaDadosImplementoAgregado,
  VistoriaEtapaDadosPessoais,
  VistoriaEtapaDadosVeiculo,
} from '@/model/vistoria-etapas.interface';

// Service
import {
  updateVistoriaAbertaStore,
  updateVistoriaStore,
} from '@/service/store';
import { useStepVistoria } from '@/stores/stepVistoria';

// Utils
import {
  caseInsensitiveObject,
  caseInsensitivePick,
  caseInsensitivePickArray,
} from '@/shared/utils/caseInsensitiveObject';
import { sleep } from '@/shared/utils/sleep';

const DADOS_PESSOAIS_KEYS: readonly (keyof VistoriaEtapaDadosPessoais &
  string)[] = [
  'idProspect',
  'nome',
  'email',
  'telefone',
  'celular',
  'tipo',
  'dataNascimento',
  'cpfcnpj',
  'rgIe',
  'sexo',
  'estadoCivil',
  'regimeCasamento',
  'representanteLegal',
  'cpfRepresentante',
  'validatedForm',
];

const DADOS_CNH_KEYS: readonly (keyof VistoriaEtapaDadosCnh & string)[] = [
  'idProspect',
  'cnh',
  'catCnh',
  'dataVencimentoCnh',
  'naoPossuiCnh',
  'validatedForm',
];

const DADOS_VEICULO_KEYS: readonly (keyof VistoriaEtapaDadosVeiculo &
  string)[] = [
  'idProposta',
  'anoExercicioDut',
  'qtdPortas',
  'cor',
  'combustivel',
  'km',
  'renavam',
  'chassi',
  'quantidadePassageiro',
  'estadoPneu',
  'tipoCambio',
  'numeroMotor',
  'placaSubstituicao',
  'codTipoVeiculo',
  'cilindradas',
  'somenteAgregado',
  'validatedForm',
];

const DADOS_FINANCEIROS_KEYS: readonly (keyof VistoriaEtapaDadosFinanceiros &
  string)[] = [
  'idProposta',
  'idTipoPagamento',
  'parcelas',
  'diaVencimentoBoleto',
  'validatedForm',
];

const DADOS_ENDERECO_KEYS: readonly (keyof VistoriaEtapaDadosEndereco &
  string)[] = [
  'idProspect',
  'cep',
  'endereco',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'estado',
  'validatedForm',
];

const DADOS_IMPLEMENTO_AGREGADO_KEYS: readonly (keyof VistoriaEtapaDadosImplementoAgregado &
  string)[] = [
  'idPropostaComplemento',
  'descricao',
  'marca',
  'modelo',
  'placa',
  'chassi',
  'observacao',
  'valorCobertura',
  'validatedForm',
];

const FORMAS_KEYS: readonly (keyof Formas & string)[] = [
  'idTipoPagamento',
  'descricao',
  'parcelas',
];

const normalizeFormasPagamento = (value: unknown): FormasPagamento => {
  const formasPagamento = caseInsensitiveObject(
    (value || {}) as Record<string, unknown>,
  );

  return {
    formas: caseInsensitivePickArray<Formas>(formasPagamento.formas, FORMAS_KEYS),
    cartoes: Array.isArray(formasPagamento.cartoes)
      ? formasPagamento.cartoes
      : [],
  };
};

const normalizeVistoriaResponse = (value: unknown, chave: string): Vistoria => {
  const source = caseInsensitiveObject(value as Record<string, unknown>);

  return {
    chave,
    idEmpresa: source.idEmpresa as number,
    idProposta: source.idProposta as number,
    proposalNumber: source.proposalNumber as number,
    isZero: source.isZero as boolean,
    idGrupo: source.idGrupo as number,
    idProspect: source.idProspect as number,
    nome: source.nome as string,
    veiculoMarca: source.veiculoMarca as string | undefined,
    veiculoModelo: source.veiculoModelo as string | undefined,
    placa: source.placa as string | undefined,
    chassi: source.chassi as string | undefined,
    id_Status: source.id_Status as number,
    motivoReprovacaoGeral: source.motivoReprovacaoGeral as string | undefined,
    realizarAssinatura: source.realizarAssinatura as boolean,
    tipoProposta: source.tipoProposta as string,
    tipoApolice: source.tipoApolice as number,
    dataAceite: source.dataAceite as string | undefined,
    diasVencimento: source.diasVencimento as string,
    assinatura: source.assinatura as string | undefined,
    contratos: caseInsensitivePickArray(
      source.contratos,
      ['idModelo', 'modeloContrato', 'tipoContrato', 'aceite'],
    ),
    dadosPessoais: caseInsensitivePick<VistoriaEtapaDadosPessoais>(
      source.dadosPessoais,
      DADOS_PESSOAIS_KEYS,
    ),
    validatedDadosPessoais: source.validatedDadosPessoais as boolean,
    dadosCNH: caseInsensitivePick<VistoriaEtapaDadosCnh>(
      source.dadosCNH,
      DADOS_CNH_KEYS,
    ),
    validatedDadosCNH: source.validatedDadosCNH as boolean,
    dadosVeiculos: caseInsensitivePick<VistoriaEtapaDadosVeiculo>(
      source.dadosVeiculos,
      DADOS_VEICULO_KEYS,
    ),
    validatedDadosVeiculos: source.validatedDadosVeiculos as boolean,
    dadosFinanceiros: caseInsensitivePick<VistoriaEtapaDadosFinanceiros>(
      source.dadosFinanceiros,
      DADOS_FINANCEIROS_KEYS,
    ),
    validatedDadosFinanceiros: source.validatedDadosFinanceiros as boolean,
    dadosEndereco: caseInsensitivePick<VistoriaEtapaDadosEndereco>(
      source.dadosEndereco,
      DADOS_ENDERECO_KEYS,
    ),
    validatedDadosEndereco: source.validatedDadosEndereco as boolean,
    combustiveis: caseInsensitivePickArray(source.combustiveis, ['descricao']),
    cores: caseInsensitivePickArray(source.cores, ['descricao']),
    consultor: caseInsensitivePick<VistoriaConsultor>(source.consultor, [
      'nome',
      'celular',
      'whatsapp',
    ]),
    agregado: source.agregado
      ? caseInsensitivePickArray<VistoriaEtapaDadosImplementoAgregado>(
          source.agregado,
          DADOS_IMPLEMENTO_AGREGADO_KEYS,
        )
      : null,
    implemento: source.implemento
      ? caseInsensitivePickArray<VistoriaEtapaDadosImplementoAgregado>(
          source.implemento,
          DADOS_IMPLEMENTO_AGREGADO_KEYS,
        )
      : null,
    formasPagamento: normalizeFormasPagamento(source.formasPagamento),
    execucao: source.execucao as VistoriaExecucao | undefined,
  };
};

/**
 * Salva a vistória no IndexedDB
 * @param value dados da vistoria
 */
const saveDataIndexedDB = async (value: Vistoria) => {
  if (!db.isOpen()) {
    await db.open();
  }

  value.dateReceived = new Date();

  value.usedExceptionInspectionToken =
    value.usedExceptionInspectionToken || useStepVistoria().exceptionTokenValid;

  await db.vistoria.put(value);
  await updateVistoriaStore(value.chave);
};

/**
 * Recupera os dados da Vistoria no IndexedDB
 * @param chave chave da proposta
 */
export async function getDataVistoriaIndexedDB(chave: string) {
  const result = await db.vistoria.where('chave').equals(chave).first();

  if (result) {
    return Promise.resolve(result);
  }

  return Promise.resolve(null);
}

/**
 * Recupera os dados da Vistoria no IndexedDB
 * @param idProposta id da proposa
 */
export async function getDataVistoriaIndexedDBByIdProposa(idProposta: number) {
  const result = await db.vistoria
    .where('idProposta')
    .equals(idProposta)
    .first();

  if (result) {
    return Promise.resolve(result);
  }

  return Promise.resolve(null);
}

/**
 * verifica se aquela vistoria esta aberta na API
 * @param chave chave da proposta
 */
export async function checaValidadeVistoriaAPI(chave: string) {
  const url = `${
    import.meta.env.VITE_APP_API_VIDA_URL
  }/prior-inspections/${chave}/check-inspection-validity`;
  const method = 'GET';
  const headers = {
    Authorization: `Bearer ${import.meta.env.VITE_APP_API_VIDA_TOKEN}`,
  };

  const requestOptions = {
    url,
    method,
    headers,
  };

  const { data, error } = await doRequest(requestOptions);

  if (error) {
    throw 'Entre em contato com o seu corretor!';
  }

  if (data) {
    const { valid } = caseInsensitiveObject(data as { valid: boolean });
    updateVistoriaAbertaStore(valid);
    return valid;
  }

  return null;
}

/**
 * Recupera os dados da Vistoria na API
 * @param chave chave da proposta
 */
export function getDataVistoriaAPI(chave: string) {
  return new Promise<Vistoria | null>((resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria/${chave}`;
    const method = 'GET';

    const requestOptions = {
      url,
      method,
    };

    doRequest(requestOptions).then(async (res) => {
      if (res.error) {
        return reject(res.error);
      }

      if (res.data) {
        const result = normalizeVistoriaResponse(res.data, chave);

        await saveDataIndexedDB(result);
        resolve(result);
      }

      resolve(null);
    });
  });
}

/**
 * Recupera a 'chave' da vistoria/proposta na API
 * @param idProposta id da proposta
 */
export function getChaveVistoriaAPI(idProposta: number) {
  return new Promise<{ chave: string } | null>((resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria//chave/${idProposta}`;
    const method = 'GET';

    const requestOptions = {
      url,
      method,
    };

    doRequest(requestOptions).then(async (res) => {
      if (res.error) {
        return reject(res.error);
      }

      if (res.data) {
        const data = caseInsensitiveObject(res.data as { chave: string });
        resolve({ chave: data.chave });
      }

      reject();
    });
  });
}

/**
 * Recuperar o status da Proposta/Vistoria
 * @param idProposta id da Proposta
 * @param usedExceptionInspectionToken indica se o token de exceção foi validado nesta sessão
 * @returns {Promise<boolean>}
 */
export function getStatusVistoriaAPI(
  idProposta: number,
  usedExceptionInspectionToken: boolean = false,
) {
  // eslint-disable-next-line no-async-promise-executor
  return new Promise<boolean>(async (resolve, reject) => {
    const url = `${import.meta.env.VITE_URL_API}/vistoria/atualiza-status`;
    const method = 'POST';
    const data = { idProposta, usedExceptionInspectionToken };

    let retries = 0;
    let success = false;
    const maxRetries = 30;
    let errorResult;

    const requestOptions = {
      url,
      method,
      data,
    };

    while (retries < maxRetries && !success) {
      try {
        const res = await doRequest(requestOptions);

        if (res.data) {
          success = true;
          const { result } = caseInsensitiveObject(
            res.data as { result: boolean },
          );
          return resolve(result);
        }

        if (res.error) {
          errorResult = res.error;
          await sleep(2000);
          retries++;
        }
      } catch (error) {
        errorResult = error;
      }
    }

    reject(errorResult);
  });
}

/**
 * Recupera o objeto execucao da vistoria
 * @param vistoria dados da vistoria
 * @returns
 */
const getExecucaoVistoria = async (vistoria: Vistoria | null) => {
  if (vistoria) {
    if (vistoria.execucao) {
      return vistoria.execucao;
    }

    return {} as VistoriaExecucao;
  }
};

/**
 * Salva a permissão do usuário
 * @param chave da proposta/vistoria
 */
export async function savePermissions(chave: string) {
  const vistoria = await getDataVistoriaIndexedDB(chave);
  const execucao = await getExecucaoVistoria(vistoria);

  if (vistoria && execucao) {
    const { idProposta } = vistoria;
    execucao.permissions = true;

    await db.vistoria.update(idProposta, { execucao });
    await updateVistoriaStore(chave);
  }
}

/**
 * Marca, para auditoria, que o token de exceção foi usado nesta
 * vistoria — persiste `true` e nunca desmarca. Não confundir com o campo
 * `exceptionTokenValid` do store (só em memória, controla se a galeria está
 * liberada agora; reavaliado a cada carregamento, sem sobreviver a reload).
 * @param chave da proposta/vistoria
 */
export async function saveUsedExceptionInspectionToken(chave: string) {
  const vistoria = await getDataVistoriaIndexedDB(chave);

  if (vistoria) {
    const { idProposta } = vistoria;

    await db.vistoria.update(idProposta, { usedExceptionInspectionToken: true });
    await updateVistoriaStore(chave);
  }
}

/**
 * Salva a quantidade de etapas preparadas p/ o sync
 * @param chave da proposta/vistoria
 * @param countPrepared a quantidade de etapas preparadas
 */
export async function saveCountPrepared(chave: string, countPrepared: number) {
  try {
    const vistoria = await getDataVistoriaIndexedDB(chave);
    const execucao = await getExecucaoVistoria(vistoria);

    if (vistoria && execucao) {
      const { idProposta } = vistoria;
      execucao.countPrepared = countPrepared;
      await db.vistoria.update(idProposta, { execucao });
    }
  } catch (error) {
    console.error('Error saving count prepared:', error);
  }
}
